import { ChangeDetectionStrategy, Component, forwardRef, Input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { ReadFieldValuePipe } from '@shared/pipes/object.pipe';
import { ObjectUtils } from '@shared/utils/object.utils';
import { IconComponent } from '@ui/icon/icon.component';
import { cloneDeep } from 'lodash-es';
import { BaseFormControl } from '../base-form-control.component';
import { FormControlErrorsComponent } from '../form-control-errors/form-control-errors.component';

@Component({
  selector: 'app-auto-complete',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.scss'],
  imports: [
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormControlErrorsComponent,
    IconComponent,
    MatInputModule,
    ReadFieldValuePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoCompleteComponent),
      multi: true,
    },
  ],
})
export class AutoCompleteComponent extends BaseFormControl<number | string | unknown> implements OnChanges {
  @Input() items: unknown[] = [];
  @Input() bindLabel = 'label';
  @Input() bindValue = 'value';

  arrowIcon = signal('arrow_drop_down');
  filteredItems = signal<unknown[]>([]);
  displayValue = '';

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.items) {
      this.filter();
    }
  }

  private getDisplayLabel(item: unknown): string {
    return ObjectUtils.readFieldValue(item, this.bindLabel);
  }

  protected updateArrowIcon(): void {
    this.arrowIcon.update((icon) => (icon === 'arrow_drop_down' ? 'arrow_drop_up' : 'arrow_drop_down'));
  }

  protected onPanelOpened(): void {
    this.updateArrowIcon();
    // À l'ouverture, afficher toutes les options même si une valeur est déjà sélectionnée ;
    // le filtrage ne s'applique qu'à la saisie (onInputChange)
    this.filteredItems.set(this.items);
  }

  protected onInputChange(input: string): void {
    this.displayValue = input;
    this.filter();
  }

  filter(): void {
    const filterValue = cloneDeep(this.displayValue?.toString()?.trim() || '');
    let items = this.items;
    if (filterValue?.length) {
      items = items.filter((item) => this.getDisplayLabel(item).toLowerCase().includes(filterValue.toLowerCase()));
    }
    this.filteredItems.set(items);
  }

  selectItem(selectedLabel: string): void {
    this.displayValue = selectedLabel;
    this.filter();
    const value = this.filteredItems().find((item) => this.getDisplayLabel(item) === this.displayValue);
    if (this.value !== value) {
      this.value = value;
    }
  }

  writeValue(obj: unknown): void {
    this.value = obj;
    this.displayValue = this.getDisplayLabel(obj);
    this.filter();
  }

  openOrClosePanel(trigger: MatAutocompleteTrigger, event: MouseEvent): void {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    if (trigger.panelOpen) {
      trigger.closePanel();
    } else {
      trigger.openPanel();
    }
  }

  clearInput(auto?: MatAutocomplete): void {
    auto?.options?.forEach((opt) => opt.deselect());
    this.writeValue(null);
  }

  protected trackByFn = (index: number, item: unknown) => this.getDisplayLabel(item) || index;
}
